/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Sample datasets", description = "Explore Hightouch using the B2B SaaS and B2C eCommerce sample data sources—no warehouse setup required.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Sample datasets",
        description: "Explore Hightouch using the B2B SaaS and B2C eCommerce sample data sources—no warehouse setup required.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sample-data",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Add a sample source",
          "depth": 0,
          "id": "add-a-sample-source"
        }, {
          "value": "Create a model",
          "depth": 0,
          "id": "create-a-model"
        }, {
          "value": "Add a model",
          "depth": 1,
          "id": "add-a-model"
        }, {
          "value": "Select the sample source",
          "depth": 1,
          "id": "select-the-sample-source"
        }, {
          "value": "Choose a modeling method",
          "depth": 1,
          "id": "choose-a-modeling-method"
        }, {
          "value": "Use the pre-populated sample queries",
          "depth": 1,
          "id": "use-the-pre-populated-sample-queries"
        }, {
          "value": "Finalize the model",
          "depth": 1,
          "id": "finalize-the-model"
        }, {
          "value": "Send data with a sync",
          "depth": 0,
          "id": "send-data-with-a-sync"
        }, {
          "value": "Use in Customer Studio",
          "depth": 0,
          "id": "use-in-customer-studio"
        }, {
          "value": "Create a parent model",
          "depth": 1,
          "id": "create-a-parent-model"
        }, {
          "value": "Build audiences, journeys, and more",
          "depth": 1,
          "id": "build-audiences-journeys-and-more"
        }, {
          "value": "Sample dataset contents",
          "depth": 0,
          "id": "sample-dataset-contents"
        }, {
          "value": "B2B SaaS",
          "depth": 1,
          "id": "b2b-saas"
        }, {
          "value": "B2C eCommerce",
          "depth": 1,
          "id": "b2c-ecommerce"
        }, {
          "value": "Limitations",
          "depth": 0,
          "id": "limitations"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      img: "img",
      h3: "h3",
      ol: "ol",
      code: "code",
      em: "em",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides two built-in ", _jsx(_components.strong, {
          children: "Sample datasets"
        }), " that let you explore the product without connecting your own data warehouse. They're hosted on a read-only database that Hightouch manages for you, so there are no credentials, networking, or permissions to configure."]
      }), "\n", _jsx(_components.p, {
        children: "Sample datasets are ideal for:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Running your first sync end-to-end before your production source is ready."
        }), "\n", _jsx(_components.li, {
          children: "Evaluating Hightouch during a trial or POC."
        }), "\n", _jsxs(_components.li, {
          children: ["Learning how ", _jsx(_components.a, {
            href: "/getting-started/concepts#models",
            children: "models"
          }), ", ", _jsx(_components.a, {
            href: "/getting-started/concepts#syncs",
            children: "syncs"
          }), ", and ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer Studio"
          }), " work against realistic data."]
        }), "\n", _jsxs(_components.li, {
          children: ["Testing a new ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination"
          }), " integration without touching production data."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Sample datasets are real, customer-facing sources—not internal-only demo\ntooling. They appear in every workspace at the end of the source list, and\nusage of sample sources is excluded from your workspace's billing and\nanalytics metrics."
        })
      }), "\n", _jsx(_components.p, {
        children: "Two sample sources are available:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "B2B SaaS"
          }), ": models a typical SaaS business with users and organizations, useful for testing CRM, marketing, and product analytics destinations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "B2C eCommerce"
          }), ": contains customer profiles and cart events data for a B2C eCommerce company that retails shoe brands, useful for testing ad platforms, email, loyalty, and retention destinations."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "add-a-sample-source",
        children: "Add a sample source"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/add-source.png",
          alt: "Sources overview page with the Add source button highlighted"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Scroll to the bottom of the source catalog. The ", _jsx(_components.strong, {
          children: "Sample datasets"
        }), " section contains both ", _jsx(_components.strong, {
          children: "B2B SaaS"
        }), " and ", _jsx(_components.strong, {
          children: "B2C eCommerce"
        }), ". Select the one you want to use."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/source-sample-dataset.png",
          alt: "Sample datasets section at the end of the source catalog"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Because the underlying database is managed by Hightouch, there are no credentials to enter and no connection test to run. Hightouch skips directly to the ", _jsx(_components.strong, {
          children: "Finalize source"
        }), " step."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/finalize-sample-source-settings.png",
          alt: "Finalize settings for the sample source"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Give the source a name, review the default permissions, and click ", _jsx(_components.strong, {
          children: "Finish"
        }), ". The source is ready to use immediately."]
      }), "\n", _jsx(_components.h2, {
        id: "create-a-model",
        children: "Create a model"
      }), "\n", _jsxs(_components.p, {
        children: ["Models define what data to pull from your source. You can build ", _jsx(_components.a, {
          href: "/models/creating-models",
          children: "models"
        }), " on top of a sample source using the ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "SQL editor"
        }), " or the ", _jsx(_components.a, {
          href: "/models/table-selector",
          children: "visual table selector"
        }), ". ", _jsx(_components.a, {
          href: "/extensions/dbt-models",
          children: "dbt"
        }), ", ", _jsx(_components.a, {
          href: "/extensions/looker-models",
          children: "Looker"
        }), ", and ", _jsx(_components.a, {
          href: "/extensions/sigma",
          children: "Sigma"
        }), " modeling methods aren't supported on sample sources."]
      }), "\n", _jsx(_components.h3, {
        id: "add-a-model",
        children: "Add a model"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/models?from=docs",
          children: [_jsx(_components.strong, {
            children: "Models"
          }), " overview page"]
        }), " and click ", _jsx(_components.strong, {
          children: "Add model"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/activation-add-model.png",
          alt: "All models page with the Add model button highlighted"
        })
      }), "\n", _jsx(_components.h3, {
        id: "select-the-sample-source",
        children: "Select the sample source"
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.strong, {
          children: "Select source"
        }), " step, choose your ", _jsx(_components.strong, {
          children: "B2B SaaS"
        }), " or ", _jsx(_components.strong, {
          children: "B2C eCommerce"
        }), " sample source. Sample sources are clearly labeled with a ", _jsx(_components.strong, {
          children: "Sample"
        }), " badge."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/sample-data-source.png",
          alt: "Selecting a sample source on the Select source step"
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-a-modeling-method",
        children: "Choose a modeling method"
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.strong, {
          children: "Define model"
        }), " step, choose either the ", _jsx(_components.strong, {
          children: "SQL query"
        }), " editor or the ", _jsx(_components.strong, {
          children: "Table selector"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/sample-select-modeling-method.png",
          alt: "Choosing between the SQL query editor and Table selector"
        })
      }), "\n", _jsx(_components.h3, {
        id: "use-the-pre-populated-sample-queries",
        children: "Use the pre-populated sample queries"
      }), "\n", _jsxs(_components.p, {
        children: ["When you pick a sample source in the SQL editor, Hightouch shows a ", _jsx(_components.strong, {
          children: "Sample queries"
        }), " panel with ready-to-run examples tailored to the dataset. Click a query to populate the editor, then click ", _jsx(_components.strong, {
          children: "Preview results"
        }), " to run it."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/preview-sample-results.png",
          alt: "Preview results rendered from a B2B SaaS sample query"
        })
      }), "\n", _jsx(_components.p, {
        children: "The included sample queries are a good starting point for common use cases—product-led upsell, abandoned-cart retargeting, seasonal campaigns, VIP audiences—and can be edited freely."
      }), "\n", _jsxs(_components.p, {
        children: ["For a full reference on writing model queries, see ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "Create models with the SQL editor"
        }), " or ", _jsx(_components.a, {
          href: "/models/table-selector",
          children: "Create models with the table selector"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "finalize-the-model",
        children: "Finalize the model"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you're happy with the preview, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to move to the ", _jsx(_components.strong, {
          children: "Finalize model"
        }), " step. Give the model a name, select a ", _jsx(_components.strong, {
          children: "Primary key"
        }), ", and click ", _jsx(_components.strong, {
          children: "Finish"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/finalize-sample-model.png",
          alt: "Finalizing settings for a model built on the B2B SaaS sample source"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Your new model appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/models?from=docs",
          children: [_jsx(_components.strong, {
            children: "Models"
          }), " overview page"]
        }), " and is ready to use in syncs."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/sample-model-columns.png",
          alt: "Columns view of a finished model built on the B2B SaaS sample source"
        })
      }), "\n", _jsx(_components.h2, {
        id: "send-data-with-a-sync",
        children: "Send data with a sync"
      }), "\n", _jsxs(_components.p, {
        children: ["With a model in place, you can create a ", _jsx(_components.a, {
          href: "/syncs/overview",
          children: "sync"
        }), " that delivers sample data to any destination in your workspace."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsxs(_components.a, {
              href: "https://app.hightouch.com/syncs?from=docs",
              children: [_jsx(_components.strong, {
                children: "Syncs"
              }), " overview page"]
            }), " and click ", _jsx(_components.strong, {
              children: "Add sync"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["On the ", _jsx(_components.strong, {
              children: "Select model"
            }), " step, pick the model you just built on top of the sample source."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/sample-data/sync-sample-model.png",
              alt: "Selecting the sample model on the Add sync flow"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Pick a destination. If you don't have one connected yet, ", _jsx(_components.a, {
              href: "/destinations/google-sheets",
              children: "Google Sheets"
            }), " is the fastest way to see results—it's a ", _jsx(_components.a, {
              href: "/pricing/ss-pricing",
              children: "lite destination"
            }), " and easy to set up."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Map fields from your sample model to the destination's schema."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Set a schedule and click ", _jsx(_components.strong, {
              children: "Finish"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For a full walkthrough, see ", _jsx(_components.a, {
          href: "/syncs/create-your-first-sync",
          children: "Create your first sync"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "use-in-customer-studio",
        children: "Use in Customer Studio"
      }), "\n", _jsxs(_components.p, {
        children: ["Sample sources also work with ", _jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio"
        }), ", so you can try audiences, journeys, and other activation features without real warehouse data."]
      }), "\n", _jsx(_components.h3, {
        id: "create-a-parent-model",
        children: "Create a parent model"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Customer Studio → Schema"
        }), " and switch the source selector at the top of the page to your sample source. If no parent models exist yet, click ", _jsx(_components.strong, {
          children: "Create parent model"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/schema-select-sample-data.png",
          alt: "Selecting the sample source in the Customer Studio Schema tab"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.strong, {
          children: "Select table"
        }), " step of the ", _jsx(_components.strong, {
          children: "New parent model"
        }), " flow, the ", _jsx(_components.strong, {
          children: "Sample queries"
        }), " panel is pre-populated with starter queries. Pick one to auto-fill the SQL editor, then click ", _jsx(_components.strong, {
          children: "Preview results"
        }), " to check your data before continuing."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/create-new-sample-parent-model.png",
          alt: "Previewing a B2C eCommerce sample query in the parent model flow"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.strong, {
          children: "Configuration"
        }), " step, give the parent model a name, pick a ", _jsx(_components.strong, {
          children: "Primary key"
        }), ", and optionally select primary and secondary labels used when previewing audiences. Click ", _jsx(_components.strong, {
          children: "Create parent model"
        }), " to finish."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/configure-parent-model-customer-studio.png",
          alt: "Configuring a parent model built on the B2C eCommerce sample source"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Your new parent model appears in the ", _jsx(_components.strong, {
          children: "Schema"
        }), " tab with all its columns visible and ready for audience building."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/sample-data/customer-studio-sample-model-columns.png",
          alt: "Columns view of a parent model built on the B2C eCommerce sample source"
        })
      }), "\n", _jsx(_components.h3, {
        id: "build-audiences-journeys-and-more",
        children: "Build audiences, journeys, and more"
      }), "\n", _jsx(_components.p, {
        children: "Once a parent model exists, you can use it just like any production source to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Build ", _jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "audiences"
          }), "—for example, VIP customers with items still in their cart, or Self-Serve organizations approaching their storage limit."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add ", _jsx(_components.a, {
            href: "/customer-studio/schema#add-a-related-model",
            children: "related models"
          }), " (like purchases, subscriptions, or events) to enrich filtering."]
        }), "\n", _jsxs(_components.li, {
          children: ["Create ", _jsx(_components.a, {
            href: "/customer-studio/traits",
            children: "traits"
          }), " for reusable calculated fields such as lifetime value or last-seen timestamp."]
        }), "\n", _jsxs(_components.li, {
          children: ["Run lifecycle ", _jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "journeys"
          }), " across multiple destinations."]
        }), "\n", _jsxs(_components.li, {
          children: ["Sync audiences to any connected destination with ", _jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Customer Studio syncs"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See the ", _jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio overview"
        }), " for an end-to-end walkthrough."]
      }), "\n", _jsx(_components.h2, {
        id: "sample-dataset-contents",
        children: "Sample dataset contents"
      }), "\n", _jsxs(_components.p, {
        children: ["Each sample database is organized under the ", _jsx(_components.code, {
          children: "public"
        }), " schema. Use the ", _jsx(_components.strong, {
          children: "table selector"
        }), " when creating a model to see the full list of tables and columns—the schemas may expand over time as Hightouch adds more examples."]
      }), "\n", _jsx(_components.h3, {
        id: "b2b-saas",
        children: "B2B SaaS"
      }), "\n", _jsxs(_components.p, {
        children: ["Models a SaaS business with records for users and their organizations. The dataset includes tables such as ", _jsx(_components.code, {
          children: "public.users"
        }), " and ", _jsx(_components.code, {
          children: "public.organizations"
        }), ", with attributes like ", _jsx(_components.code, {
          children: "type"
        }), ", ", _jsx(_components.code, {
          children: "segment"
        }), ", ", _jsx(_components.code, {
          children: "total_storage_used_mb"
        }), ", ", _jsx(_components.code, {
          children: "total_users_invited"
        }), ", and ", _jsx(_components.code, {
          children: "documents_uploaded_l30d"
        }), ". This makes it a good fit for testing:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "product-led growth workflows (for example, surfacing upsell opportunities to Self-Serve accounts nearing a storage limit)"
        }), "\n", _jsx(_components.li, {
          children: "lifecycle marketing to admins and inactive users"
        }), "\n", _jsx(_components.li, {
          children: "CRM and marketing-automation syncs to destinations like Salesforce, HubSpot, Marketo, Braze, or Segment"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Sample queries"
        }), " panel includes starter queries for ", _jsx(_components.em, {
          children: "Product-led sales upsell opportunities"
        }), ", ", _jsx(_components.em, {
          children: "Lifecycle marketing to inactive admins"
        }), ", and ", _jsx(_components.em, {
          children: "New Business Tier product introduction"
        }), ". A typical starter query—surfacing Self-Serve organizations using more than 50 GB of storage—looks like:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " public.organizations\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " type ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Self-Serve'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CAST"
          }), "(total_storage_used_mb ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "INT"
          }), ") ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50000"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "b2c-ecommerce",
        children: "B2C eCommerce"
      }), "\n", _jsxs(_components.p, {
        children: ["Models a direct-to-consumer retailer selling shoe brands. The dataset includes a ", _jsx(_components.code, {
          children: "public.customers"
        }), " table with attributes such as ", _jsx(_components.code, {
          children: "customer_id"
        }), ", ", _jsx(_components.code, {
          children: "first_name"
        }), ", ", _jsx(_components.code, {
          children: "last_name"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), ", ", _jsx(_components.code, {
          children: "city"
        }), ", ", _jsx(_components.code, {
          children: "ltv"
        }), ", ", _jsx(_components.code, {
          children: "brand_affinity"
        }), ", ", _jsx(_components.code, {
          children: "VIP"
        }), " tier, and ", _jsx(_components.code, {
          children: "items_in_cart"
        }), ", alongside cart-event data. This makes it a good fit for testing:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "audience-building workflows (for example, VIP customers or high-intent shoppers)"
        }), "\n", _jsx(_components.li, {
          children: "abandoned-cart retargeting to ad platforms like Meta, Google Ads, or TikTok"
        }), "\n", _jsx(_components.li, {
          children: "email and SMS campaigns through tools like Iterable, Klaviyo, or Attentive"
        }), "\n", _jsx(_components.li, {
          children: "loyalty and retention use cases"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Sample queries"
        }), " panel includes starter queries for ", _jsx(_components.em, {
          children: "Abandoned cart retargeting ads"
        }), ", ", _jsx(_components.em, {
          children: "Seasonal Rebook campaign - Nano X2"
        }), ", and ", _jsx(_components.em, {
          children: "Annual VIP celebration event in Florida"
        }), ". A typical starter query—pulling non-VIP customers with items still in their cart—looks like:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " public.customers\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " VIP ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Normal'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CAST"
          }), "(items_in_cart ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "int"
          }), ") ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "limitations",
        children: "Limitations"
      }), "\n", _jsx(_components.p, {
        children: "Sample sources are intentionally simple. A few things to know:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Read-only."
          }), " You can't write to a sample database, create tables, or modify its schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Shared across workspaces."
          }), " The underlying database is the same for every Hightouch customer. Don't store anything you expect to be private in a sample source—you shouldn't be able to, but it's worth stating plainly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lightning sync engine isn't supported."
          }), " Sample sources always use the Basic sync engine (Hightouch's ", _jsx(_components.code, {
            children: "LocalDiffPlanner"
          }), "). Lightning engine features such as ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          }), ", ", _jsx(_components.a, {
            href: "/match-booster/overview",
            children: "Match Booster"
          }), ", and ", _jsx(_components.a, {
            href: "https://hightouch.com/platform/identity-resolution",
            children: "Identity Resolution"
          }), " therefore aren't available on syncs powered by a sample source."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Not for production."
          }), " Sample datasets are meant for exploration and demos only. When you're ready to run real workloads, ", _jsx(_components.a, {
            href: "/sources/overview",
            children: "connect your own source"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If a sync against a sample source reports a ", _jsx(_components.code, {
            children: "permission denied"
          }), " error, that\nusually means the sync was configured to use the Lightning engine. Switch the\nsync to the Basic engine (or recreate the sync) and the error will clear."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsx(_components.p, {
        children: "With your sample source connected, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Follow the ", _jsx(_components.a, {
            href: "/syncs/create-your-first-sync",
            children: "Create your first sync"
          }), " tutorial end-to-end."]
        }), "\n", _jsxs(_components.li, {
          children: ["Pick a destination from the ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination catalog"
          }), " and map sample data to it."]
        }), "\n", _jsxs(_components.li, {
          children: ["Explore ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer Studio"
          }), " to build audiences and journeys on top of sample data."]
        }), "\n", _jsxs(_components.li, {
          children: ["When you're ready to move to production data, review the ", _jsx(_components.a, {
            href: "/sources/overview",
            children: "Sources overview"
          }), " and add your own warehouse."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
